import React, { ReactNode } from "react";
import { truncate } from "lodash";
import styled from "styled-components";
import { shorten } from "@backpacker/number";
import TooltipContent from "@charty/tooltip";
import {
  FontWeight,
  content,
  isColor,
  transparentize,
  useTheme,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { parse } from "@tootools/emoji";
import SafeHTML from "@ui-kit/safe-html";
import Tooltip from "@ui-kit/tooltip";

const Root = styled.span.attrs<{
  normalizedWeight: number;
  color?: string;
}>(({ normalizedWeight }) => ({
  style: {
    fontSize: normalizedWeight * 100 + "%",
    opacity: normalizedWeight,
  },
}))<{
  normalizedWeight: number;
  color?: string;
  clickable: boolean;
  isHidden?: boolean;
}>`
  color: ${(props) => props.color};
  border: 2px solid currentColor;
  cursor: ${(props) =>
    props.clickable ? "var(--cursor-magic-wand)" : "default"};
  margin: 0.15em 0.5em;
  padding: 0.5em 1em;
  font-weight: ${FontWeight.Normal};
  border-radius: 2em;
  display: inline-block;
  white-space: nowrap;
  background-color: ${(props) =>
    props.isHidden ? "rgba(108, 121, 149, 0.6)" : "inherit"};
  :hover {
    background-color: ${(props) => transparentize(0.85, props.color)};
    opacity: 1 !important;
  }
`;

interface IProps {
  weight: number;
  label: string;
  normalizedWeight: number;
  locale: Locale;
  onClick?: () => void;
  color?: string;
  tooltip?: (serie: {
    label: string;
    color: string;
    weight: number;
  }) => ReactNode;
  animated?: boolean;
  isHidden?: boolean;
}

const WordCloud = ({
  label,
  color,
  normalizedWeight,
  onClick,
  weight,
  tooltip,
  locale,
  animated,
  isHidden,
}: IProps) => {
  const theme = useTheme();
  const defaultColor =
    color && isColor(color) ? color : content({ theme }, "secondary");
  return (
    <Tooltip
      disabled={animated}
      tooltip={
        tooltip ? (
          tooltip({ label, color, weight })
        ) : (
          <TooltipContent label={label} content={shorten(locale, weight)} />
        )
      }
    >
      <SafeHTML
        as={Root}
        normalizedWeight={normalizedWeight}
        isHidden={isHidden}
        color={defaultColor}
        onClick={onClick}
        clickable={!!onClick}
        html={truncate(parse(label))}
      />
    </Tooltip>
  );
};

export default WordCloud;
